import React, { useEffect, useState } from "react";
import { useAuth } from "./AuthContext";

// `||` not `??` — empty-string env must fall through to "" (same-origin relative paths).
// See PvaraRubricView.jsx for the longer rationale; audit F-26.
const API_URL = process.env.REACT_APP_API_URL || "";

const ROLE_SHORT = {
  md: "Managing Director",
  gm_policy_strategy: "GM — Policy & Strategy",
  gm_licensing_regulatory_sandbox: "GM — Licensing & Reg Sandbox",
  gm_market_supervision: "GM — Market Supervision",
  gm_enforcement: "GM — Enforcement",
  gm_technology_cyber_risk: "GM — Technology & Cyber Risk",
  gm_support_services: "GM — Support Services",
};

const REASON_LABELS = {
  no_cv_field: "No CV uploaded in application",
  placeholder_cv: "Placeholder CV reference (/uploads/default.pdf — incomplete submission)",
  cv_file_missing: "CV file not mirrored locally from prod S3 bucket",
  legacy_doc_format: "Legacy .doc format — python-docx can't parse (needs LibreOffice/antiword)",
  docx_parse_failed: "DOCX file corrupted or has unreadable embedded content",
  pdf_parse_failed_or_too_short: "PDF extracted text < 200 chars (likely scanned image, needs OCR)",
  unknown: "Other / unclassified",
};

const CONF_COLOR = {
  "High": "bg-green-100 text-green-900 border-green-200",
  "Medium-High": "bg-lime-100 text-lime-900 border-lime-200",
  "Medium": "bg-yellow-100 text-yellow-900 border-yellow-200",
  "As-reported": "bg-gray-100 text-gray-700 border-gray-200",
};

function Bar({ llm, rule, total }) {
  const llmPct = total ? (llm / total) * 100 : 0;
  const rulePct = total ? (rule / total) * 100 : 0;
  return (
    <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden flex">
      <div className="bg-purple-500" style={{ width: `${llmPct}%` }} title={`${llm} LLM-scored`} />
      <div className="bg-blue-300" style={{ width: `${rulePct}%` }} title={`${rule} rule-based only`} />
    </div>
  );
}

function PvaraMethodology() {
  const { user, apiFetch } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await apiFetch(`${API_URL}/api/pvara-rubric/methodology`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const j = await res.json();
        if (!cancelled) setData(j);
      } catch (e) {
        if (!cancelled && e.message !== "session-expired") setErr(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  if (!user) return <div className="p-6 text-center text-gray-500">Please log in.</div>;
  if (loading) return <div className="p-6 text-center text-gray-500">Loading methodology…</div>;
  if (err) return <div className="p-6 text-red-700">Failed to load: {err}</div>;
  if (!data) return null;

  const coverage = data.total ? Math.round((data.llmScored / data.total) * 100) : 0;

  return (
    <div className="space-y-4 max-w-full overflow-x-hidden">
      {/* Header */}
      <div className="bg-white rounded-xl shadow p-4 sm:p-5 border border-gray-100">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900">How the LLM Evaluated Candidates</h1>
        <p className="text-xs sm:text-sm text-gray-600 mt-1">
          Coverage stats, reasons for unscored CVs, confidence levels per line-item, and known limitations.
          This tab is the audit trail for the rubric screening.
        </p>
        <p className="text-xs sm:text-sm mt-2 rounded-lg border border-blue-200 bg-blue-50 text-blue-900 px-3 py-2">
          <b>Note — two rubrics coexist.</b> The stats below describe the <b>original</b> screening
          (Academic 30 + Experience 40 = 70 CV marks). The <b>Revised 2026</b> rubric (Academic 20 + Experience 40 = 60,
          with the evidence-of-impact deduction) runs in parallel on the “Rubric v2 (Revised)” tab and writes to separate
          fields — its per-candidate methodology (criterion evidence, deduction rationale, quoted outcomes) is shown in
          each candidate's dossier on that tab. Original scores are never modified.
        </p>
      </div>

      {/* Summary KPI cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <div className="text-xs text-gray-500 uppercase">Total applications</div>
          <div className="text-2xl font-bold mt-1">{data.total.toLocaleString()}</div>
          <div className="text-[11px] text-gray-500">across 7 step-1 roles</div>
        </div>
        <div className="bg-purple-50 border border-purple-200 rounded-xl p-4">
          <div className="text-xs text-purple-700 uppercase">LLM-scored</div>
          <div className="text-2xl font-bold text-purple-900 mt-1">{data.llmScored.toLocaleString()}</div>
          <div className="text-[11px] text-purple-700">{coverage}% coverage</div>
        </div>
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
          <div className="text-xs text-blue-700 uppercase">Rule-based only</div>
          <div className="text-2xl font-bold text-blue-900 mt-1">{data.ruleOnly.toLocaleString()}</div>
          <div className="text-[11px] text-blue-700">CV couldn't be LLM-parsed</div>
        </div>
        <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
          <div className="text-xs text-gray-500 uppercase">Data source</div>
          <div className="text-sm font-semibold mt-1 break-words">{data.dataSource.origin}</div>
          <div className="text-[11px] text-gray-500 mt-0.5">Last pulled: {data.dataSource.pulledAt}</div>
        </div>
      </div>

      {/* Source-vs-portal reconciliation */}
      {data.reconciliation && (
        <div className="bg-white rounded-xl shadow border border-gray-100 overflow-hidden">
          <div className="px-4 sm:px-5 py-3 border-b border-gray-100 font-semibold flex items-center justify-between flex-wrap gap-2">
            <span>Source ↔ Portal reconciliation</span>
            {data.reconciliation.last_ingest_at && (
              <span className="text-xs font-normal text-gray-500">
                Last ingest: {new Date(data.reconciliation.last_ingest_at).toLocaleString("en-GB",
                  { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
              </span>
            )}
          </div>
          <div className="p-4 sm:p-5">
            <div className="grid grid-cols-3 gap-3 mb-4">
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-center">
                <div className="text-[10px] text-gray-500 uppercase">In source (est.)</div>
                <div className="text-xl font-bold mt-1">{(data.reconciliation.source_total_est || 0).toLocaleString()}</div>
              </div>
              <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-center">
                <div className="text-[10px] text-green-700 uppercase">In portal</div>
                <div className="text-xl font-bold text-green-900 mt-1">{(data.reconciliation.screening_total || 0).toLocaleString()}</div>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-center">
                <div className="text-[10px] text-amber-800 uppercase">Skipped at ingest</div>
                <div className="text-xl font-bold text-amber-900 mt-1">{(data.reconciliation.ingest_skipped || 0).toLocaleString()}</div>
              </div>
            </div>
            {(data.reconciliation.skip_reasons || []).length > 0 ? (
              <div>
                <div className="text-xs font-semibold text-gray-700 mb-2">Skip breakdown</div>
                <ul className="space-y-1.5 text-xs">
                  {data.reconciliation.skip_reasons.map(s => (
                    <li key={s.reason} className="flex justify-between border-b border-gray-100 pb-1">
                      <span className="text-gray-700">
                        {s.reason === "duplicate_cnic_for_role"
                          ? "Same candidate re-applied for the same role (kept first submission)"
                          : s.reason === "missing_or_unparseable_cv_or_applicant"
                          ? "Missing or unparseable CV / applicant data"
                          : s.reason === "ingest_error"
                          ? "Ingest error (validation / DB)"
                          : s.reason}
                      </span>
                      <span className="font-semibold text-gray-900">{s.count}</span>
                    </li>
                  ))}
                </ul>
                <p className="text-[11px] text-gray-500 mt-3">
                  Per-row detail (with candidate name + CNIC) is in the <b>Audit Log</b> tab — filter by <i>Source row skipped</i>.
                </p>
              </div>
            ) : (
              <p className="text-xs text-gray-500">No skip events recorded yet.</p>
            )}
          </div>
        </div>
      )}

      {/* Per-role coverage */}
      <div className="bg-white rounded-xl shadow border border-gray-100 overflow-hidden">
        <div className="px-4 sm:px-5 py-3 border-b border-gray-100 font-semibold">Coverage per role</div>
        <div className="p-4 sm:p-5 space-y-3">
          {data.byRole.map(r => {
            const pct = r.total ? Math.round((r.llm / r.total) * 100) : 0;
            return (
              <div key={r.roleId}>
                <div className="flex items-center justify-between text-xs mb-1">
                  <div className="font-medium text-gray-800">{ROLE_SHORT[r.roleId] || r.roleId}</div>
                  <div className="text-gray-500">
                    <span className="text-purple-700 font-semibold">{r.llm}</span> LLM
                    {" · "}
                    <span className="text-blue-700 font-semibold">{r.ruleOnly}</span> rule
                    {" · "}
                    <span className="font-semibold">{r.total}</span> total
                    {" · "}
                    <span className="font-semibold">{pct}%</span>
                  </div>
                </div>
                <Bar llm={r.llm} rule={r.ruleOnly} total={r.total} />
                {r.maxLLM != null && (
                  <div className="text-[10px] text-gray-400 mt-0.5">
                    LLM scores: mean {r.meanLLM}/70, max {r.maxLLM}/70
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Methodology stages */}
      <div className="bg-white rounded-xl shadow border border-gray-100 overflow-hidden">
        <div className="px-4 sm:px-5 py-3 border-b border-gray-100 font-semibold">Scoring pipeline</div>
        <div className="p-4 sm:p-5 space-y-4">
          {data.methodology.stages.map((stage, i) => (
            <div key={i} className="border border-gray-200 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-1">
                <div className="w-6 h-6 rounded-full bg-green-600 text-white text-xs font-bold flex items-center justify-center shrink-0">{i + 1}</div>
                <div className="font-semibold text-sm">{stage.name}</div>
              </div>
              <div className="text-xs text-gray-700 mb-2">{stage.desc}</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                <div className="bg-green-50 border border-green-200 rounded px-2 py-1.5">
                  <div className="font-semibold text-green-800 text-[10px] uppercase">Strength</div>
                  <div className="text-green-900 mt-0.5">{stage.strength}</div>
                </div>
                <div className="bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
                  <div className="font-semibold text-amber-800 text-[10px] uppercase">Weakness</div>
                  <div className="text-amber-900 mt-0.5">{stage.weakness}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Confidence per line-item */}
      <div className="bg-white rounded-xl shadow border border-gray-100 overflow-hidden">
        <div className="px-4 sm:px-5 py-3 border-b border-gray-100 font-semibold">Confidence per rubric line-item</div>
        <div className="divide-y divide-gray-100">
          {data.confidence.map((c, i) => (
            <div key={i} className="px-4 sm:px-5 py-3">
              <div className="flex items-start justify-between gap-3">
                <div className="font-medium text-sm text-gray-900">{c.field}</div>
                <span className={"text-xs font-semibold px-2 py-0.5 rounded border whitespace-nowrap " + (CONF_COLOR[c.level] || "bg-gray-100 text-gray-700")}>{c.level}</span>
              </div>
              <div className="text-xs text-gray-600 mt-1">{c.note}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Unscored reasons */}
      <div className="bg-white rounded-xl shadow border border-gray-100 overflow-hidden">
        <div className="px-4 sm:px-5 py-3 border-b border-gray-100 font-semibold flex items-center justify-between">
          <span>Why {data.ruleOnly} CVs couldn't be LLM-scored</span>
          <span className="text-xs text-gray-500 font-normal">These fall back to rule-based only</span>
        </div>
        <div className="divide-y divide-gray-100">
          {Object.entries(data.unscoredReasons).filter(([_, n]) => n > 0).sort((a, b) => b[1] - a[1]).map(([key, n]) => (
            <div key={key} className="px-4 sm:px-5 py-3">
              <button
                onClick={() => setExpanded(e => ({ ...e, [key]: !e[key] }))}
                className="w-full flex items-start justify-between gap-3 text-left"
              >
                <div>
                  <div className="font-medium text-sm text-gray-900">{REASON_LABELS[key] || key}</div>
                  <div className="text-xs text-gray-500 mt-0.5">
                    {expanded[key] ? "▾ Hide samples" : "▸ Show up to 10 affected candidates"}
                  </div>
                </div>
                <div className="text-sm font-bold text-gray-900">{n}</div>
              </button>
              {expanded[key] && data.unscoredSamples[key] && (
                <div className="mt-2 ml-2 space-y-0.5">
                  {data.unscoredSamples[key].map((s, i) => (
                    <div key={i} className="text-[11px] text-gray-600 font-mono">
                      {s.name} · {s.cnic} · {s.roleId} · rule={s.ruleScore}/70
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Limitations */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 sm:p-5">
        <div className="font-semibold text-amber-900 mb-2">⚠ Known limitations (read before relying on scores)</div>
        <ul className="text-xs text-amber-900 list-disc pl-5 space-y-1">
          {data.limitations.map((l, i) => <li key={i}>{l}</li>)}
        </ul>
      </div>

      {/* How sure? */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 sm:p-5">
        <div className="font-semibold text-blue-900 mb-2">How sure are we the scores are correct?</div>
        <div className="text-xs text-blue-900 leading-relaxed">
          <p className="mb-2">
            <b>High confidence:</b> the scores are a faithful application of the PVARA paper rubric (SCF-2026). Every point awarded can be traced to a specific line-item and tier — open any candidate's breakdown drawer to see the evidence behind each sub-score.
          </p>
          <p className="mb-2">
            <b>Medium confidence on input quality:</b> we score what the CV says. If a candidate misrepresents experience or overstates credentials, the LLM extractor may flag it (see <code>red_flags</code> in the breakdown) but cannot always detect it. CV truthfulness is out of scope.
          </p>
          <p>
            <b>By design incomplete:</b> only Sections 1 + 2 (70 of 100 marks) are CV-scoreable. Sections 3 (Impact &amp; Achievements, 15 pts) and 4 (Competence &amp; Soft Skills, 15 pts) are interview-only. A 60/70 here means the candidate is well-positioned going into interview — not that they are the final choice.
          </p>
        </div>
      </div>
    </div>
  );
}

export default PvaraMethodology;
